import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChild, ViewChildren} from '@angular/core';
import {MatSort} from '@angular/material/sort';
import {SelectionModel} from '@angular/cdk/collections';
import {MatTableDataSource} from '@angular/material/table';
import {UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {IResourceModel, MultiLanguage} from '@I14Y-ch/bfs-iop-admin-web-api-client';
import {URI_PATTERN} from 'src/app/app-constants';

@Component({
	selector: 'app-edit-table-resource-model',
	templateUrl: './edit-table-resource-model.component.html',
	styleUrls: ['./edit-table-resource-model.component.scss'],
	standalone: false
})
export class EditTableResourceModelComponent implements AfterViewInit, OnChanges {
	@ViewChild(MatSort) sort!: MatSort;
	@ViewChildren('focusInputField') focusInputFields!: QueryList<ElementRef>;
	@Input() parentForm!: UntypedFormGroup;
	@Input() dto: any;
	@Input() controlName!: string;
	@Input() maxEntries: number | undefined;
	@Input() required: boolean = false;
	@Output() edit: EventEmitter<boolean> = new EventEmitter();
	public dataSource = new MatTableDataSource<IResourceModel>([]);

	COLUMN_URI = 'uri';
	COLUMN_GERMAN = 'de';
	COLUMN_FRENCH = 'fr';
	COLUMN_ITALIAN = 'it';
	COLUMN_ENGLISH = 'en';
	COLUMN_ROMANCH = 'rm';
	COLUMN_SELECT = 'select';
	COLUMN_ACTIONS = 'actions';

	displayedColumns: string[] = [
		this.COLUMN_SELECT,
		this.COLUMN_URI,
		this.COLUMN_GERMAN,
		this.COLUMN_FRENCH,
		this.COLUMN_ITALIAN,
		this.COLUMN_ENGLISH,
		this.COLUMN_ROMANCH,
		this.COLUMN_ACTIONS
	];

	private initialRowValue: IResourceModel;
	private isEditing = false;
	private isAddMode = false;
	private rowIndex: number | undefined;
	private selection = new SelectionModel<IResourceModel>(true, []);

	constructor() {
		this.initialRowValue = this.newEntry();
	}

	ngAfterViewInit(): void {
		this.dataSource.sort = this.sort;
	}

	ngOnChanges(changes: SimpleChanges) {
		const change = changes.dto;
		if (change && change.currentValue !== undefined) {
			this.dataSource = new MatTableDataSource<IResourceModel>(this.dto);
			this.initResourceToTableData();
		}
	}

	onMasterToggle(): void {
		if (this.isAllSelected()) {
			this.selection.clear();
		} else {
			this.dataSource.data.forEach(row => this.selection.select(row));
		}
	}

	onRemoveRow(index: number): void {
		this.dataSource.data.splice(index, 1);
		this.resourceControl.removeAt(index);
		if (!this.isAddMode) {
			this.parentForm.markAsDirty();
		}
		this.refreshDatabinding();
	}

	onRemoveSelectedRows(): void {
		this.selection.selected.forEach(item => {
			const index: number = this.dataSource.data.findIndex((d: IResourceModel) => d === item);
			this.onRemoveRow(index);
		});
		this.selection = new SelectionModel<IResourceModel>(true, []);
	}

	onAddRow(): void {
		const numRows = this.dataSource.data.push(this.newEntry());

		const formArray = this.parentForm.get(this.controlName) as UntypedFormArray;
		if (formArray) {
			formArray.push(
				new UntypedFormGroup({
					uri: new UntypedFormControl('', [Validators.required, Validators.pattern(URI_PATTERN)]),
					label: new UntypedFormGroup({
						de: new UntypedFormControl(''),
						fr: new UntypedFormControl(''),
						it: new UntypedFormControl(''),
						en: new UntypedFormControl(''),
						rm: new UntypedFormControl('')
					})
				})
			);

			this.isAddMode = true;
			this.UpdateIsEditing(true);
			this.rowIndex = numRows - 1;
			this.refreshDatabinding();
			this.setFocus();
		}
	}

	type(ele: any, value: any, val: any) {
		ele[val] = value;
	}

	onDiscard() {
		if (this.isAddMode) {
			this.onRemoveRow(this.rowIndex!);
			this.isAddMode = false;
		} else {
			this.dataSource.data[this.rowIndex!] = this.copyRowValue(this.initialRowValue);
		}

		this.initResourceToTableData();
		this.refreshDatabinding();
		this.initialRowValue = this.newEntry();
		this.UpdateIsEditing(false);
	}

	onEditRow(rowIndex: number): void {
		this.initialRowValue = this.copyRowValue(this.dataSource.data[rowIndex]);
		this.UpdateIsEditing(true);
		this.rowIndex = +rowIndex;
		this.setFocus();
	}

	onSave() {
		this.initResourceToTableData();
		this.isAddMode = false;
		this.UpdateIsEditing(false);
	}

	canAdd(): boolean {
		let canAdd = !this.isEditing && !this.hasSelectedItems();
		if (this.maxEntries) {
			return canAdd && this.dataSource.data.length < this.maxEntries;
		}
		return canAdd;
	}

	canEdit(): boolean {
		return !this.isEditing;
	}

	canRemove(): boolean {
		return !this.isEditing;
	}

	canSave(rowIndex: number): boolean {
		if (this.isRowEditMode(rowIndex) && !this.isControlInvalid(rowIndex)) {
			if (this.dataSource.data[rowIndex].uri) {
				return true;
			}
		}
		return false;
	}

	canSelect(): boolean {
		return !this.isEditing;
	}

	hasSelectedRows(): boolean {
		return this.selection.hasValue();
	}

	isAllSelected(): boolean {
		const numSelected = this.selection.selected.length;
		const numRows = this.dataSource.data.length;
		return numSelected === numRows;
	}

	isEditMode(): boolean {
		return this.isEditing;
	}

	isRowEditMode(index: number): boolean {
		return this.isEditing && this.rowIndex === index;
	}

	isRowSelected(row: IResourceModel): boolean {
		return this.selection.isSelected(row);
	}

	isControlInvalid(index: number): boolean {
		const relationsArray = this.parentForm.get(this.controlName) as UntypedFormArray;
		if (relationsArray) {
			if (relationsArray.at(index)) {
				const control = relationsArray.at(index) as UntypedFormControl;
				if (control) {
					return control.invalid;
				}
			}
		}
		return false;
	}
	onToggleSelection(row: IResourceModel): void {
		this.selection.toggle(row);
	}

	private copyRowValue(source: IResourceModel): IResourceModel {
		return {
			uri: source.uri,
			label: new MultiLanguage({
				de: source?.label?.de,
				en: source?.label?.en,
				fr: source?.label?.fr,
				it: source?.label?.it,
				rm: source?.label?.rm
			})
		};
	}

	private hasSelectedItems(): boolean {
		return !this.selection.isEmpty();
	}

	private initResourceToTableData(): void {
		const formArray = new UntypedFormArray(
			this.dataSource.data.map(item => {
				if (item.label === undefined || item.label === null) {
					// initialize item.label to avoid modifidfication errors in case it is undefined
					item.label = new MultiLanguage({
						de: undefined,
						fr: undefined,
						it: undefined,
						en: undefined,
						rm: undefined
					});
				}
				return new UntypedFormGroup({
					uri: new UntypedFormControl(item.uri, [Validators.required, Validators.pattern(URI_PATTERN)]),
					label: new UntypedFormGroup({
						de: new UntypedFormControl(item.label.de),
						fr: new UntypedFormControl(item.label.fr),
						it: new UntypedFormControl(item.label.it),
						en: new UntypedFormControl(item.label.en),
						rm: new UntypedFormControl(item.label.rm)
					})
				});
			})
		);

		this.parentForm.setControl(this.controlName, formArray);
	}

	private newEntry(): IResourceModel {
		return {
			uri: undefined,
			label: new MultiLanguage({
				de: undefined,
				en: undefined,
				fr: undefined,
				it: undefined,
				rm: undefined,
			})
		};
	}

	private refreshDatabinding(): void {
		this.dataSource.filter = '';
	}

	private setFocus(): void {
		setTimeout(() => {
			this.focusInputFields.get(this.rowIndex!)?.nativeElement.focus();
		});
	}

	private UpdateIsEditing(value: boolean) {
		this.isEditing = value;
		this.edit.emit(value);
	}

	private get resourceControl(): any {
		return this.parentForm.get(this.controlName) as UntypedFormArray;
	}
}
